import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalPortal from './ModalPortal';
import MenuButton from './MenuButton';
import settings from '../core/settings';
import InstructionsBox from './InstructionsBox';

// --- Custom hook: persist setting to storage, skipping initial mount ---
function usePersistedSetting(key, value, { serialize, dispatchEvent: eventName } = {}) {
  const initRef = useRef(false);
  useEffect(() => {
    if (!initRef.current) { initRef.current = true; return; }
    try {
      const stored = serialize ? serialize(value) : value;
      if (stored === null || stored === undefined) settings.remove(key);
      else settings.set(key, stored);
      if (eventName) {
        try { window.dispatchEvent(new CustomEvent(eventName, { detail: value })); } catch (e) {}
      }
    } catch (e) {}
  }, [key, value, serialize, eventName]);
}

// --- Custom hook: sync state from external storage/settings events ---
function useSettingsSync(key, setter, { parse }) {
  useEffect(() => {
    const onSettingsChanged = (ev) => {
      try {
        if (ev?.detail?.key === key) setter(parse(ev.detail.value));
      } catch (e) {}
    };
    const onStorage = (e) => {
      if (e.key === key) {
        try { setter(parse(e.newValue)); } catch (e) { setter(parse(null)); }
      }
    };
    window.addEventListener('settingsChanged', onSettingsChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('settingsChanged', onSettingsChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, [key, setter, parse]);
}

const serializeSound = (v) => v || null; // null → remove

function LeftMenu({
  game,
  gameMode,
  gameState,
  onPlaySolo,
  onPlayZen,
  onDailyChallenge,
  dailyPlayed,
  onReviewDaily,
  dailyViewing,
  dailyPercentile,
  dailyTotalPlayers,
  onPlayWithFriends,
  onCreateLobby,
  onJoinLobby,
  onBackToMainMenu,
  onNewGame,
  onRestartGame,
  onStartGame,
  gameSeed,
  countdownValue,
  lobbyCode,
  players,
  wins = {},
  playerId,
  hostId,
  startError = '',
  pendingInvite,
  onAcceptInvite,
  onDeclineInvite,
  authUser,
  onLogin,
  onLogout,
  onOpenStats,
  backendUrl,
  authToken,
}) {
  const [playerName, setPlayerName] = useState('');
  const navigate = useNavigate();
  const [nameTouched, setNameTouched] = useState(false);
  const [lobbyCodeInput, setLobbyCodeInput] = useState('');
  const [seedInput, setSeedInput] = useState('');
  const [copiedSeed, setCopiedSeed] = useState(false);
  const [copiedLobbyCode, setCopiedLobbyCode] = useState(false);
  const [copiedInviteLink, setCopiedInviteLink] = useState(false);
  const [leaderboardView, setLeaderboardView] = useState('score');
  const [inviteNameInput, setInviteNameInput] = useState('');
  const [inviteNameTouched, setInviteNameTouched] = useState(false);
  const [dailyResult, setDailyResult] = useState(() => {
    try {
      const raw = settings.getString(settings.KEYS.DAILY_RESULT) || '';
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });
  const [fetchedPercentile, setFetchedPercentile] = useState(null);
  const [fetchedTotalPlayers, setFetchedTotalPlayers] = useState(null);

  const getTodayLocalString = () => {
    try {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const d = String(now.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    } catch (e) { return ''; }
  };

  useEffect(() => {
    const today = getTodayLocalString();
    if (
      backendUrl &&
      authToken &&
      dailyResult &&
      dailyResult.date === today &&
      dailyPercentile === null
    ) {
      console.log('[Daily] Fetching ranking from server for date:', today);
      fetch(`${backendUrl}/api/daily/ranking/${today}`, {
        headers: { 'Authorization': `Bearer ${authToken}` },
      })
        .then(res => res.json())
        .then(data => {
          console.log('[Daily] Ranking response:', data);
          if (data.success && data.has_ranking) {
            setFetchedPercentile(data.percentile);
            setFetchedTotalPlayers(data.total_players);
          }
        })
        .catch(e => console.error('[Daily] Ranking fetch error:', e));
    }
  }, [backendUrl, authToken, dailyResult, dailyPercentile]);

  // soundSetting removed; use presence of selectedSound to enable/disable
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [activeSettingsHeader, setActiveSettingsHeader] = useState('Sounds');
  const [soundFiles, setSoundFiles] = useState([]);
  const [selectedSound, setSelectedSound] = useState(() => settings.getString(settings.KEYS.SELECTED_SOUND));
  const [masterVolume, setMasterVolume] = useState(() => settings.getNumber(settings.KEYS.MASTER));
  const prevInitial = (() => settings.getNumber(settings.KEYS.MASTER))();
  const prevMasterRef = useRef(prevInitial);

  const toggleMute = () => {
    try {
      if (masterVolume > 0) {
        prevMasterRef.current = masterVolume;
        setMasterVolume(0);
      } else {
        setMasterVolume(prevMasterRef.current || 100);
      }
    } catch (e) {}
  };

  const onMasterVolumeChange = (value) => {
    const v = Number(value);
    setMasterVolume(v);
    if (v > 0) prevMasterRef.current = v;
  };

  const [sfxVolume, setSfxVolume] = useState(() => settings.getNumber(settings.KEYS.SFX));
  const [musicVolume, setMusicVolume] = useState(() => settings.getNumber(settings.KEYS.MUSIC));

  // --- Persist settings to storage (skip initial mount) ---
  usePersistedSetting(settings.KEYS.SELECTED_SOUND, selectedSound, { serialize: serializeSound });
  usePersistedSetting(settings.KEYS.MASTER, masterVolume);
  usePersistedSetting(settings.KEYS.SFX, sfxVolume);
  usePersistedSetting(settings.KEYS.MUSIC, musicVolume);

  // --- Sync state from external storage/settings events ---
  useSettingsSync(settings.KEYS.DAILY_RESULT, setDailyResult, {
    parse: (val) => {
      if (!val) return null;
      try { return typeof val === 'string' ? JSON.parse(val) : val; } catch (e) { return null; }
    },
  });

  useEffect(() => {
    if (pendingInvite && pendingInvite.name) {
      setInviteNameInput(pendingInvite.name);
      setInviteNameTouched(false);
    }
    if (!pendingInvite) {
      setInviteNameInput('');
      setInviteNameTouched(false);
    }
  }, [pendingInvite]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuOpen && !e.target.closest('.auth-menu-container')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    // load sounds manifest from public folder; try common path then fallback to nested path if needed
    const tryLoad = async () => {
      const paths = ['/sounds/sounds.json', '/Sounds/frontend/public/sounds/sounds.json'];
      for (const p of paths) {
        try {
          const res = await fetch(p);
          if (!res.ok) continue;
          const list = await res.json();
          const normalized = (list || []).map((item) =>
            typeof item === 'string' ? { file: item, name: item } : item
          );
          setSoundFiles(normalized);
          if (normalized && normalized.length > 0) {
            const firstAllowed = normalized.find(item => item.allowAsClear !== false) || normalized[0];
            try {
              setSelectedSound(prev => {
                const stored = settings.getString(settings.KEYS.SELECTED_SOUND);
                const current = prev || (stored || '').trim();
                const isDisallowed = current && normalized.some(item => item.file === current && item.allowAsClear === false);
                if (!current || isDisallowed) {
                  return firstAllowed.file;
                }
                return prev || firstAllowed.file;
              });
            } catch (e) {
              setSelectedSound(firstAllowed.file);
            }
          }
          return;
        } catch (e) {
          continue;
        }
      }
      // no manifest found — leave list empty
    };
    tryLoad();
  }, []);

  // --- Copy to clipboard helper ---
  const copyToClipboard = (text, setCopiedState) => {
    navigator.clipboard.writeText(text);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  // --- Reusable render helpers ---

  const renderCopyableDisplay = (label, value, copiedState, setCopiedState, extraButtons) => (
    <div className="control-group">
      <label>{label}</label>
      <div className="seed-display-with-copy">
        <div className="seed-display">{value}</div>
        <button className="icon-button-small" onClick={() => copyToClipboard(value, setCopiedState)} title="Copy">📋</button>
        {extraButtons}
        {copiedState && <span className="copied-indicator">Copied!</span>}
      </div>
    </div>
  );

  const renderSeedDisplay = () => renderCopyableDisplay('Game Seed', gameSeed, copiedSeed, setCopiedSeed);

  const renderLobbyCodeDisplay = ({ showInviteLink = false } = {}) => {
    const inviteButton = showInviteLink ? (
      <>
        <button
          className="icon-button-small"
          onClick={() => {
            try {
              const url = `${window.location.origin}${window.location.pathname}?invite=${encodeURIComponent(lobbyCode)}`;
              copyToClipboard(url, setCopiedInviteLink);
              try { window.history.replaceState({}, document.title, window.location.pathname); } catch (e) {}
            } catch (e) {}
          }}
          title="Copy invite link"
        >
          🔗
        </button>
        {copiedInviteLink && <span className="copied-indicator">Copied!</span>}
      </>
    ) : null;

    return renderCopyableDisplay('Lobby Code', lobbyCode, copiedLobbyCode, setCopiedLobbyCode, inviteButton);
  };

  const renderScoreboard = ({ wrapperClassName = 'left-menu-player-list' } = {}) => {
    const hasWins = Object.values(wins).some(w => w > 0);
    const showingWins = leaderboardView === 'wins';

    const sortedPlayers = showingWins
      ? [...Object.values(players)].sort((a, b) => (wins[b.player_id] || 0) - (wins[a.player_id] || 0))
      : [...Object.values(players)].sort((a, b) => b.score - a.score);

    const toggle = hasWins ? (
      <div className="leaderboard-toggle">
        <button
          className={`leaderboard-toggle-btn ${!showingWins ? 'active' : ''}`}
          onClick={() => setLeaderboardView('score')}
        >
          Score
        </button>
        <button
          className={`leaderboard-toggle-btn ${showingWins ? 'active' : ''}`}
          onClick={() => setLeaderboardView('wins')}
        >
          Wins
        </button>
      </div>
    ) : null;

    const board = (
      <div className="left-menu-scoreboard">
        {sortedPlayers.map((player, index) => (
          <div
            key={player.player_id}
            className={`score-item ${player.player_id === playerId ? 'current-player' : ''} ${
              index === 0 ? 'winner' : ''
            }`}
          >
            <span className="rank">{index + 1}</span>
            <span className="name">{player.player_name}</span>
            <span className="score">{showingWins ? (wins[player.player_id] || 0) : player.score}</span>
          </div>
        ))}
      </div>
    );
    const content = (
      <>
        {toggle}
        {board}
      </>
    );
    return wrapperClassName ? <div className={wrapperClassName}>{content}</div> : content;
  };

  const renderVolumeRow = (label, value, setter) => (
    <div className="volume-row">
      <div className="volume-label">{label}</div>
      <input
        className="volume-range"
        type="range"
        min="0"
        max="100"
        value={value}
        onChange={(e) => setter(Number(e.target.value))}
      />
      <div className="volume-value">{value}%</div>
    </div>
  );

  const renderMenuHeader = (title, { subtitle, useIconButton = false } = {}) => (
    <div className="menu-header">
      <div className={useIconButton ? 'menu-header-row' : undefined} style={!useIconButton ? { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' } : undefined}>
        {useIconButton ? (
          <MenuButton compact className="back-icon-button" onClick={onBackToMainMenu} aria-label="Back" icon={"←"} />
        ) : (
          <MenuButton compact className="back-icon" onClick={onBackToMainMenu} aria-label="Back" icon={"←"} />
        )}
        <h3>{title}</h3>
      </div>
      {subtitle && <p className="menu-subtitle">{subtitle}</p>}
    </div>
  );

  // --- Invite modal ---

  const renderInviteModal = () => {
    if (!pendingInvite || (pendingInvite.name && pendingInvite.name.trim())) return null;
    const trimmed = inviteNameInput.trim();
    const isValid = trimmed.length > 0 && trimmed.length <= 16;
    return (
      <ModalPortal>
        <div className="settings-modal-overlay" onClick={() => onDeclineInvite && onDeclineInvite()}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()} style={{ width: 420, maxWidth: '92%', height: 'auto' }}>
            <div className="settings-modal-header">
              <h3>Join Lobby</h3>
            </div>
            <div className="settings-modal-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ flex: 1 }} className="control-group input-with-error">
                  {inviteNameTouched && !isValid && (
                    <div className="input-error-text">Name must be 1-16 characters.</div>
                  )}
                  <input
                    className="join-lobby-input"
                    type="text"
                    placeholder="Enter your display name"
                    value={inviteNameInput}
                    onChange={(e) => { setInviteNameInput(e.target.value); setInviteNameTouched(true); }}
                    maxLength={16}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                  <MenuButton onClick={() => { setInviteNameTouched(true); if (isValid && onAcceptInvite) onAcceptInvite(trimmed, pendingInvite.code); }} disabled={!isValid}>Join</MenuButton>
                  <MenuButton onClick={() => onDeclineInvite && onDeclineInvite()}>Cancel</MenuButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ModalPortal>
    );
  };

  // --- Settings modal ---

  const renderSettingsModal = () => (
    <ModalPortal>
      <div className="settings-modal-overlay" onClick={() => setSettingsOpen(false)}>
        <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
          <div className="settings-modal-header">
            <h3>Settings</h3>
            <button className="icon-button" onClick={() => setSettingsOpen(false)} title="Close">✖</button>
          </div>

          <div className="settings-modal-body">
            <div className="settings-headers">
              <button
                className={`settings-header ${activeSettingsHeader === 'Sounds' ? 'active' : ''}`}
                onClick={() => setActiveSettingsHeader('Sounds')}
              >
                Sounds
              </button>
              {game?.SettingsPanel && (
                <button
                  className={`settings-header ${activeSettingsHeader === 'Graphics' ? 'active' : ''}`}
                  onClick={() => setActiveSettingsHeader('Graphics')}
                >
                  Graphics
                </button>
              )}
            </div>

            <div className="settings-content">
              {activeSettingsHeader === 'Sounds' ? (
                <div className="control-group">
                  <label>Sounds</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {renderVolumeRow('Master Volume', masterVolume, setMasterVolume)}
                    {renderVolumeRow('Sound Effects', sfxVolume, setSfxVolume)}
                    {renderVolumeRow('Music', musicVolume, setMusicVolume)}

                    <div className="setting-row">
                      <div className="setting-label">Clearing Sound</div>
                      <select
                        className="setting-select"
                        value={selectedSound}
                        onChange={(e) => setSelectedSound(e.target.value)}
                      >
                        <option value="">None</option>
                        {soundFiles.length === 0 && <option value="">(no sounds)</option>}
                        {soundFiles.filter(f => f.allowAsClear !== false).map((f) => (
                          <option key={f.file} value={f.file}>{f.name}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <MenuButton
                        onClick={async () => {
                          if (!selectedSound) return;
                          try {
                            const vol = (masterVolume/100) * (sfxVolume/100);
                            const AudioManager = await import('../core/AudioManager');
                            await AudioManager.initAudio();
                            // preload then play
                            await AudioManager.loadSound(selectedSound, `/sounds/${selectedSound}`);
                            AudioManager.playSound(selectedSound, { volume: vol });
                          } catch (e) {
                            // fallback: best-effort HTMLAudio
                            try { const a = new Audio(`/sounds/${selectedSound}`); a.play().catch(()=>{}); } catch (e) {}
                          }
                        }}
                      >
                        Preview
                      </MenuButton>
                    </div>
                  </div>
                </div>
              ) : activeSettingsHeader === 'Graphics' && game?.SettingsPanel ? (
                <game.SettingsPanel />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );

  // --- Footer ---

  const renderFooter = () => (
    <div className="left-menu-footer">
      <div className="footer-controls">
        <div className="auth-menu-container">
          {authUser ? (
            <>
              <button
                className="auth-avatar-button"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                aria-label="User menu"
              >
                {(authUser.username || 'U')[0].toUpperCase()}
              </button>
              {userMenuOpen && (
                <div className="auth-dropdown">
                  <div className="auth-dropdown-name">{authUser.username}</div>
                  <button
                    className="auth-dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      if (onOpenStats) onOpenStats();
                    }}
                  >
                    Statistics
                  </button>
                  <div className="auth-dropdown-separator" />
                  <button
                    className="auth-dropdown-logout"
                    onClick={() => {
                      setUserMenuOpen(false);
                      onLogout();
                    }}
                  >
                    Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <MenuButton compact className="auth-login-button" onClick={onLogin} aria-label="Login" icon={"👤"} />
          )}
        </div>
        <MenuButton compact className="settings-bottom" onClick={() => setSettingsOpen(true)} aria-label="Settings" icon={"⚙️"} />

        {/* footer seed removed; shown above seed input in relevant menus */}

        <div className="speaker-control">
          <MenuButton compact className="speaker-button" onClick={toggleMute} aria-pressed={masterVolume === 0} aria-label={masterVolume === 0 ? 'Unmute' : 'Mute'} icon={masterVolume === 0 ? '🔇' : '🔊'} />
          <input className="speaker-range" type="range" min="0" max="100" value={masterVolume} onChange={(e) => onMasterVolumeChange(e.target.value)} />
        </div>
      </div>
      <div style={{ marginTop: 12 }}>
        <InstructionsBox
          usePortal={true}
          parentEl={typeof document !== 'undefined' ? document.getElementById('fruitbox-grid') : null}
          maxWidth={420}
          text={game?.instructions}
        />
      </div>
      {settingsOpen && renderSettingsModal()}
    </div>
  );

  // --- Derived state ---

  const trimmedPlayerName = playerName.trim();
  const isNameValid = trimmedPlayerName.length > 0 && trimmedPlayerName.length <= 16;
  const showNameError = nameTouched && !isNameValid;
  const isHost = playerId === hostId;

  // Render invite modal if present
  const inviteModalEl = renderInviteModal();

  // --- Screen renders ---

  // Main menu (landing page)
  if (gameMode === '' && gameState === 'home') {
    const today = getTodayLocalString();
    const hasDailyResultToday = dailyResult && dailyResult.date === today;
    const displayPercentile = dailyPercentile ?? fetchedPercentile;
    const displayTotalPlayers = dailyTotalPlayers ?? fetchedTotalPlayers;
    
    return (
      <div className="left-menu">
        <div className="menu-top-bar">
          <MenuButton compact icon={"🏠"} onClick={() => navigate('/')} ariaLabel="Home" />
        </div>
        <div className="menu-header">
          <h2>{game?.name ?? 'Tensai'}</h2>
          <p className="menu-subtitle">{game?.description ?? 'Choose your game mode'}</p>
        </div>
        <div className="menu-buttons">
          {(game?.modes ?? []).map((mode) => {
            const callbackMap = { onPlaySolo, onPlayZen, onDailyChallenge, onPlayWithFriends };
            const onClick = callbackMap[mode.callbackProp];
            if (mode.dailyAware) {
              return (
                <React.Fragment key={mode.key}>
                  <MenuButton
                    onClick={() => { if (onClick) onClick(); }}
                    icon={mode.icon}
                    ariaLabel={mode.label}
                  >
                    {dailyPlayed ? `Replay ${mode.label}` : mode.label}
                  </MenuButton>
                  {hasDailyResultToday && (
                    <div className="daily-result-summary" style={{ marginTop: 8, fontSize: '0.85rem', color: '#ddd', padding: '8px', background: 'rgba(0,0,0,0.3)', borderRadius: '6px' }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>Today's Daily Challenge</div>
                      <div>Score: <strong>{dailyResult.score ?? '—'}</strong> pts</div>
                      {displayPercentile !== null && displayTotalPlayers !== null && (
                        <div style={{ marginTop: 4, color: '#8f8' }}>
                          Ranking: <strong>Top {Math.max(0.1, 100 - displayPercentile).toFixed(1)}%</strong>
                          <span style={{ opacity: 0.7, marginLeft: 4 }}>({displayTotalPlayers} players)</span>
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            }
            return (
              <MenuButton key={mode.key} onClick={onClick} icon={mode.icon} ariaLabel={mode.label}>
                {mode.label}
              </MenuButton>
            );
          })}
        </div>

        {renderFooter()}
      </div>
    );
  }

  // Solo game mode menu
  if (gameMode === 'solo') {
    return (
      <div className="left-menu">
        {renderMenuHeader(dailyViewing ? 'Daily Challenge' : 'Solo Mode')}

        <div className="menu-controls">
          <div className="control-group">
            {gameSeed && renderSeedDisplay()}

            <label>Seed (optional)</label>
            <input
              type="text"
              placeholder="Enter custom seed"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              maxLength={10}
            />
          </div>
          <MenuButton onClick={() => onNewGame(seedInput)}>Start New Game</MenuButton>
          {dailyResult && dailyViewing && dailyResult.date === getTodayLocalString() && (
            <div className="daily-result-summary" style={{ marginTop: 8, fontSize: '0.9rem', color: '#ddd' }}>
              <div>Today's Daily Score: <strong>{dailyResult.score ?? '—'}</strong> pts</div>
              {(dailyPercentile ?? fetchedPercentile) !== null && (dailyTotalPlayers ?? fetchedTotalPlayers) !== null && (
                <div style={{ marginTop: 4, color: '#8f8' }}>
                  Ranking: <strong>Top {Math.max(0.1, 100 - (dailyPercentile ?? fetchedPercentile)).toFixed(1)}%</strong>
                  <span style={{ opacity: 0.7, marginLeft: 6 }}>({dailyTotalPlayers ?? fetchedTotalPlayers} players)</span>
                </div>
              )}
              <div style={{ fontSize: '0.8rem', opacity: 0.85 }}>{dailyResult.date || ''}</div>
            </div>
          )}
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Zen mode menu
  if (gameMode === 'zen') {
    return (
      <div className="left-menu">
        {renderMenuHeader('Zen Mode')}

        <div className="menu-controls">
          {gameSeed && renderSeedDisplay()}

          <div className="control-group">
            <label>Seed (optional)</label>
            <input
              type="text"
              placeholder="Enter custom seed"
              value={seedInput}
              onChange={(e) => setSeedInput(e.target.value)}
              maxLength={10}
            />
          </div>
          <MenuButton onClick={() => onNewGame(seedInput)}>Start New Game</MenuButton>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Multiplayer mode menu
  if (gameMode === 'multiplayer' && gameState === 'home') {
    return (
      <div className="left-menu">
        {renderMenuHeader('Multiplayer', { useIconButton: true })}

        {inviteModalEl}

        <div className="menu-controls">
          <div className="control-group">
            <div className="input-with-error">
              {showNameError && (
                <div className="input-error-text">Name must be 1-16 characters.</div>
              )}
              <input
                type="text"
                placeholder="Enter your display name"
                value={playerName}
                onChange={(e) => {
                  setPlayerName(e.target.value);
                  setNameTouched(true);
                }}
                onBlur={() => setNameTouched(true)}
                maxLength={16}
              />
            </div>
          </div>

          <MenuButton onClick={() => onCreateLobby(playerName)} disabled={!isNameValid}>Create Lobby</MenuButton>

          <div className="control-group">
            <div className="join-lobby-row">
              <input
                className="join-lobby-input"
                type="text"
                placeholder="Enter lobby code"
                value={lobbyCodeInput}
                onChange={(e) => setLobbyCodeInput(e.target.value.toUpperCase())}
                maxLength={6}
              />
              <MenuButton onClick={() => onJoinLobby(playerName, lobbyCodeInput)} disabled={!isNameValid || !lobbyCodeInput.trim()}>Join</MenuButton>
            </div>
          </div>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Lobby waiting menu (for multiplayer)
  if (gameMode === 'multiplayer' && gameState === 'lobby') {
    return (
      <div className="left-menu">
        {renderMenuHeader('Lobby', { subtitle: 'Share the code with friends' })}

        <div className="menu-controls">
          {renderLobbyCodeDisplay({ showInviteLink: true })}
          {renderSeedDisplay()}

          {renderScoreboard()}

          {isHost ? (
            <div className="post-game-controls">
              <MenuButton onClick={onStartGame}>Start Game</MenuButton>
              {startError && (
                <div className="start-error" role="status" aria-live="polite">{startError}</div>
              )}
            </div>
          ) : (
            <div className="waiting-for-host">
              <p>Waiting for host to start the game...</p>
              <MenuButton onClick={onBackToMainMenu}>Leave Lobby</MenuButton>
            </div>
          )}
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Multiplayer countdown menu
  if (gameMode === 'multiplayer' && gameState === 'countdown') {
    const safeCountdown = Number.isFinite(countdownValue) ? countdownValue : 0;

    return (
      <div className="left-menu">
        {renderMenuHeader('Get Ready', { subtitle: `Starting in ${safeCountdown}s` })}

        <div className="menu-controls">
          {renderLobbyCodeDisplay()}
          {renderSeedDisplay()}
        </div>

        {renderScoreboard()}

        {renderFooter()}
      </div>
    );
  }

  // Multiplayer game in progress menu
  if (gameMode === 'multiplayer' && gameState === 'playing') {
    return (
      <div className="left-menu">
        {renderMenuHeader('Game On', { subtitle: 'Play the board and score points' })}

        <div className="menu-controls">
          {renderLobbyCodeDisplay()}
          {renderSeedDisplay()}
        </div>

        {renderScoreboard()}

        <div className="post-game-controls">
          <MenuButton onClick={onBackToMainMenu}>Leave Game</MenuButton>
        </div>
        {renderFooter()}
      </div>
    );
  }

  // Multiplayer game finished menu
  if (gameMode === 'multiplayer' && gameState === 'finished') {
    return (
      <div className="left-menu">
        <div className="menu-header">
          <div className="menu-header-row">
            <button className="icon-button" onClick={onBackToMainMenu} title="Back">←</button>
            <h3>Game Over</h3>
          </div>
          <p className="menu-subtitle">Final scores</p>
        </div>

        <div className="menu-controls">
          {renderLobbyCodeDisplay()}
          {renderSeedDisplay()}
        </div>

        {renderScoreboard({ wrapperClassName: null })}

        {isHost ? (
          <div className="post-game-controls">
            <MenuButton onClick={onRestartGame}>Restart Game</MenuButton>
            <MenuButton onClick={onBackToMainMenu}>Back to Menu</MenuButton>
          </div>
        ) : (
          <div className="waiting-for-host">
            <p>Waiting for host to restart the game...</p>
            <MenuButton disabled>Waiting...</MenuButton>
          </div>
        )}
        {renderFooter()}
      </div>
    );
  }

  return null;
}

export default LeftMenu;
